import { mods } from "../../utils/mods";

const ibanLengthForCountry = {
	AD: 24,
	AE: 23,
	AL: 28,
	AO: 25,
	AT: 20,
	AZ: 28,
	BA: 20,
	BE: 16,
	BF: 27,
	BG: 22,
	BH: 22,
	BI: 16,
	BJ: 28,
	BR: 29,
	CH: 21,
	CI: 28,
	CM: 27,
	CR: 21,
	CV: 25,
	CY: 28,
	CZ: 24,
	DE: 22,
	DK: 18,
	DO: 28,
	DZ: 24,
	EE: 20,
	ES: 24,
	FI: 18,
	FO: 18,
	FR: 27,
	GB: 22,
	GE: 22,
	GI: 23,
	GL: 18,
	GR: 27,
	GT: 28,
	HR: 21,
	HU: 28,
	IE: 22,
	IL: 23,
	IR: 26,
	IS: 26,
	IT: 27,
	JO: 30,
	KW: 30,
	KZ: 20,
	LB: 28,
	LI: 21,
	LT: 20,
	LU: 20,
	LV: 21,
	MC: 27,
	MD: 24,
	ME: 22,
	MG: 27,
	MK: 19,
	ML: 28,
	MR: 27,
	MT: 31,
	MU: 30,
	MZ: 25,
	NL: 18,
	NO: 15,
	PK: 24,
	PL: 28,
	PS: 29,
	PT: 25,
	QA: 29,
	RO: 24,
	RS: 22,
	SA: 24,
	SE: 24,
	SI: 19,
	SK: 24,
	SM: 27,
	SN: 28,
	TL: 23,
	TN: 24,
	TR: 26,
	UA: 29,
	VG: 24,
	XK: 20,
};

const ibanUtils = {
	format: (iban: string): string => {
		return iban.toUpperCase();
	},
	getCountryCode: (iban: string): string => {
		const formattedIban = ibanUtils.format(iban);
		const leadingUpperCaseLetters = formattedIban.match(/^[A-Z]{2}/);

		return leadingUpperCaseLetters ? leadingUpperCaseLetters[0] : "";
	},
	getCheckDigits: (iban: string): string => {
		const checkDigits = iban.substr(2, 2).match(/^\d{2}$/);

		return checkDigits ? checkDigits[0] : "";
	},
	rearrange: (countryCode: string, checkDigits: string, remaningIban: string): string => {
		return remaningIban + countryCode + checkDigits;
	},
	convertLettersToIntigers: (rearrangedIban: string): string => {
		return rearrangedIban.replace(/[A-Z]/g, char => {
			return (char.charCodeAt(0) - 55).toString();
		});
	},
	reformat: (formattedIban: string, countryCode: string): string => {
		const checkDigits = ibanUtils.getCheckDigits(formattedIban);
		const remaningIban = formattedIban.substr(4, formattedIban.length).replace(/[^A-Z\d]/g, "");
		const rearrangedIban = ibanUtils.rearrange(countryCode, checkDigits, remaningIban);

		return ibanUtils.convertLettersToIntigers(rearrangedIban);
	},
	hasExpectedIbanLength: (iban: string): boolean => {
		const formattedIban = ibanUtils.format(iban);
		const countryCode = ibanUtils.getCountryCode(formattedIban);
		const expectedIbanLength = ibanLengthForCountry[countryCode as keyof typeof ibanLengthForCountry];

		return formattedIban.length === expectedIbanLength;
	},
	mod97check: (formattedIban: string): boolean => {
		const countryCode = ibanUtils.getCountryCode(formattedIban);
		const reformatIban = ibanUtils.reformat(formattedIban, countryCode);
		const mod97check = mods.mod97(reformatIban);

		return mod97check === 1;
	},
	isValid: (iban: string): boolean => {
		const hasRightLength = ibanUtils.hasExpectedIbanLength(iban);
		const formattedIban = ibanUtils.format(iban);
		const mod97check = ibanUtils.mod97check(formattedIban);

		return hasRightLength && mod97check;
	},
	compileValidResponseObject: (iban: string, countryCode: string): { input: string; country: string } => {
		return {
			input: iban,
			country: countryCode,
		};
	},
	getValidResponseObject: (iban: string) => {
		const countryCode = ibanUtils.getCountryCode(iban);

		return ibanUtils.compileValidResponseObject(iban, countryCode);
	},
};

export class IBAN {
	static validate(iban: string) {
		const isValidIBAN = ibanUtils.isValid(iban);

		return isValidIBAN ? ibanUtils.getValidResponseObject(iban) : null;
	}
}
